/**
 * A small, immutable wrapper that lets you build a value through a series of
 * transformation steps in a readable, left-to-right "pipeline" style.
 *
 * Each {@link Chain} instance holds a single value. Calling {@link Chain#map}
 * does not mutate the current instance; instead it returns a brand new
 * {@link Chain} wrapping the transformed value. This keeps every step pure and
 * side-effect free with respect to the chain itself.
 *
 * The original TypeScript version is generic (`Chain<TCurrent>`) and carries the
 * value's type through the chain. JavaScript has no static types, so the
 * generics are dropped, but the runtime behaviour is identical. The `@template`
 * JSDoc tags below mirror the original type parameters so editors with
 * TypeScript-powered IntelliSense can still infer types from usage.
 *
 * @template TCurrent The type of the value currently held by the chain.
 *
 * @example
 * // Transform a value through several steps and unwrap the result.
 * const result = Chain.start(2)
 *   .map((n) => n + 3)      // 5
 *   .map((n) => n * 10)     // 50
 *   .map((n) => `total: ${n}`)
 *   .end();                 // "total: 50"
 *
 * @example
 * // Start an empty chain (value is `undefined`) and build it up.
 * const greeting = Chain.start()
 *   .map(() => 'world')
 *   .map((name) => `hello, ${name}`)
 *   .end();                 // "hello, world"
 */
export class Chain {
  /**
   * Creates a new chain holding the given value.
   *
   * This constructor is intentionally **private by convention** — it is marked
   * `@private` and should never be called directly with `new Chain(...)`.
   * Always create a chain through the {@link Chain.start} factory instead. This
   * mirrors the `private constructor` of the TypeScript original and keeps the
   * public surface limited to {@link Chain.start}, {@link Chain#map} and
   * {@link Chain#end}.
   *
   * @private
   * @param {TCurrent} value The value to wrap in this chain.
   */
  constructor(value) {
    /**
     * The wrapped value for this step of the chain.
     *
     * It is assigned once in the constructor and never reassigned, making each
     * {@link Chain} instance effectively immutable.
     *
     * @private
     * @readonly
     * @type {TCurrent}
     */
    this.value = value;
  }

  /**
   * Starts a new chain.
   *
   * Call with no argument to begin an empty chain whose value is `undefined`,
   * or pass an initial value to seed the chain with it. This is the only
   * supported way to obtain a {@link Chain} instance.
   *
   * @template TInitial The type of the initial value (inferred from `value`).
   * @param {TInitial} [value] The optional value to seed the chain with. When
   *   omitted, the chain starts with `undefined`.
   * @returns {Chain<TInitial | undefined>} A new chain wrapping `value` (or
   *   `undefined` when no value is supplied).
   *
   * @example
   * Chain.start();        // Chain<undefined>
   * Chain.start(42);      // Chain wrapping 42
   * Chain.start('hello'); // Chain wrapping "hello"
   */
  static start(value) {
    return new Chain(value);
  }

  /**
   * Applies a transformation function to the current value and returns a new
   * chain wrapping the result.
   *
   * The current chain is left untouched; `map` is pure and always produces a
   * fresh {@link Chain}, so chains can be safely branched and reused.
   *
   * @template TNext The type of the value returned by `fn`.
   * @param {(value: TCurrent) => TNext} fn A function that receives the current
   *   value and returns the next value in the chain.
   * @returns {Chain<TNext>} A new chain wrapping the value returned by `fn`.
   *
   * @example
   * Chain.start(5)
   *   .map((n) => n * 2)  // Chain wrapping 10
   *   .map((n) => n - 1); // Chain wrapping 9
   */
  map(fn) {
    return new Chain(fn(this.value));
  }

  /**
   * Ends the chain and unwraps the final value.
   *
   * Call this once you have applied all the transformations you need to pull
   * the underlying value out of the {@link Chain}.
   *
   * @returns {TCurrent} The value currently held by the chain.
   *
   * @example
   * const value = Chain.start(1).map((n) => n + 1).end(); // 2
   */
  end() {
    return this.value;
  }
}